import { useViewerLocale } from "@/lib/translator";
import formatPrice from "@/lib/utils/format-price";
import BillingDetailLine from "@/pages/(app)/returns/refund/components/billing-details/billing-detail-line.tsx";
import {
  getDiscountBonusAmount,
  getTotalNewOrder,
} from "@/utils/return-requests.ts";
import useTranslation from "@babackapp/translations/use-translation";
import { useCurrentReturnRequest } from "../../../utils/current-return-request-context";
import useLineItemsState from "../../hooks/use-line-items-state-context";
import { useNotIncludedTaxLineItem } from "../../hooks/use-not-included-tax-line-item";
import { useRefundLineItems } from "../../hooks/use-refund-line-items-context";
import { useReturnShippingCost } from "../../hooks/use-return-shipping-cost";
import { useTotalDiscount } from "../../hooks/use-total-discount";
import { useRefundSelectedArticles } from "../../hooks/use-total-to-refund";
import { useRefundContext } from "../../use-refund-context";
import { useRefundLineItemsContainARefund } from "@/pages/(app)/returns/refund/hooks/use-refund-line-items-contain-a-refund.ts";

function useRefundLineItemsContainADiscount() {
  const { lineItemsState } = useLineItemsState();
  const { items: draftLineItems } = useRefundLineItems();
  return draftLineItems.some((lineItem) => {
    const itemState = lineItemsState[lineItem.line_item_id];
    return (
      lineItem.resolution === "discount" && itemState.total.selectedQuantity > 0
    );
  });
}

export default function BillingDetails() {
  const t = useTranslation();
  const {
    doNotChargeForRefund,
    doNotRefundInitialShipping,
    forceRefundInitialShipping,
  } = useRefundContext();
  const { refundAmount, setRefundAmount } = useRefundContext();
  const { discountAmount, setDiscountAmount } = useRefundContext();
  const locale = useViewerLocale();
  const returnRequest = useCurrentReturnRequest();
  const notIncludedTaxLineItemValueDiscount =
    useNotIncludedTaxLineItem("discount");
  const notIncludedTaxLineItemValueRefund = useNotIncludedTaxLineItem("refund");
  const returnShippingCostValue = useReturnShippingCost();
  const currency = returnRequest.currency.customerCurrency;
  const containARefund = useRefundLineItemsContainARefund();
  const containADiscount = useRefundLineItemsContainADiscount();

  const totalDiscountAmount = useTotalDiscount();
  const bonusDiscountAmount = getDiscountBonusAmount(returnRequest, false, {
    notValidatedByLogistician: true,
  });
  const totalToRefund = useRefundSelectedArticles({
    withCharges: false,
    chargeForRefund: false,
  });

  const containAPayment =
    returnRequest.financial_informations.reshipping.presentmentAmount !== 0;

  const hasNewOrder =
    !!returnRequest.exchange?.newProductForExchangeCredit?.length;

  if (!containADiscount && !containARefund) {
    return null;
  }

  return (
    <div className="flex flex-col space-y-4 bg-muted rounded-lg p-4">
      <span className="text-sm font-semibold mb-2">Refund Recap</span>
      {containAPayment && (
        <BillingDetailLine
          label={t("returnDetail.reshippingExecuted")}
          price={
            returnRequest.financial_informations.reshipping.presentmentAmount
          }
          currency={currency}
        />
      )}
      {containARefund && (
        <BillingDetailLine
          label={t("returnDetail.confirmModal.refund")}
          price={(totalToRefund || 0) - notIncludedTaxLineItemValueRefund}
          currency={currency}
        />
      )}
      {containADiscount && (
        <BillingDetailLine
          label={t("returnDetail.confirmModal.voucherToIssue")}
          price={
            totalDiscountAmount -
            bonusDiscountAmount -
            notIncludedTaxLineItemValueDiscount
          }
          currency={currency}
        />
      )}
      {containADiscount && (
        <BillingDetailLine
          label={t("returnDetail.confirmModal.bonusVoucher")}
          price={bonusDiscountAmount}
          currency={currency}
        />
      )}

      <BillingDetailLine
        label={"Taxes non incluses"}
        price={
          notIncludedTaxLineItemValueDiscount +
          notIncludedTaxLineItemValueRefund
        }
        currency={currency}
      />

      {!returnRequest.financial_informations.return_shipping_paid.done &&
        containARefund &&
        returnRequest.financial_informations.return_shipping_paid.method ===
          "refund" && (
          <BillingDetailLine
            label={t("returnDetail.confirmModal.refundReturnShippingCost")}
            price={returnShippingCostValue}
            currency={currency}
            isStroke={doNotChargeForRefund}
          />
        )}
      {!returnRequest.financial_informations.return_shipping_paid.done &&
        containADiscount &&
        returnRequest.financial_informations.return_shipping_paid.method ===
          "discount" && (
          <BillingDetailLine
            label={t("returnDetail.confirmModal.discountReturnShippingCost")}
            price={returnShippingCostValue}
            currency={currency}
            isStroke={doNotChargeForRefund}
          />
        )}

      {returnRequest.financial_informations.initial_shipping_refunded
        .sourceAmount > 0 &&
        (containARefund || containADiscount) && (
          <BillingDetailLine
            label={`${t("returnDetail.confirmModal.refundShippingCost")} (max: ${formatPrice({ amount: returnRequest.financial_informations.initial_shipping_refunded.sourceAmount, currency, locale })})`}
            price={
              forceRefundInitialShipping
                ? returnRequest.financial_informations.initial_shipping_refunded
                    .sourceAmount
                : returnRequest.financial_informations.initial_shipping_refunded
                    .presentment_amount
            }
            currency={currency}
            isStroke={doNotRefundInitialShipping || !forceRefundInitialShipping}
          />
        )}
      {hasNewOrder && (
        <BillingDetailLine
          label={t("returnDetail.confirmModal.totalNewOrder")}
          price={getTotalNewOrder(returnRequest)}
          currency={currency}
        />
      )}
      {containARefund && (
        <BillingDetailLine
          editable
          label={t("returnDetail.confirmModal.totalRefund")}
          price={refundAmount}
          currency={currency}
          onChange={setRefundAmount}
        />
      )}
      {containADiscount && (
        <BillingDetailLine
          editable
          label={t("returnDetail.confirmModal.totalDiscount")}
          price={discountAmount}
          currency={currency}
          onChange={setDiscountAmount}
        />
      )}
    </div>
  );
}
